import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import moment from "moment";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Switch,
  TextField,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ImageCustom from "components/ImageCustom/ImageCustom";
import { getImageUrl } from "api/AttachmentAPI";

function LayerTable({ droppableId, layers, label, onEdit, onDelete, onDragEnd }) {
  const [imageMap, setImageMap] = useState({});

  useEffect(() => {
    const fetchImages = async () => {
      const fetchTasks = layers.map((item) => {
        if (item.attachment?.encryptedId) {
          return getImageUrl(`/api/attachments/downloadFile/${item.attachment.encryptedId}`).then(
            (url) => ({
              encryptedId: item.attachment.encryptedId,
              url,
            })
          );
        }
        return null;
      });

      const results = await Promise.all(fetchTasks);
      const newMap = {};
      results.forEach((res) => {
        if (res?.url) {
          newMap[res.encryptedId] = res.url;
        }
      });
      setImageMap(newMap);
    };

    if (layers.length > 0) fetchImages();
  }, [layers]);

  return (
    <Box>
      <Typography fontWeight="bold" fontSize="16px" mb={0.5} color="text.primary">
        {label}
      </Typography>

      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <DragDropContext onDragEnd={(result) => onDragEnd(result)}>
          <Droppable droppableId={droppableId}>
            {(dropProvided) => (
              <Box sx={{ maxHeight: 320, overflowY: "auto" }}>
                <Table
                  {...dropProvided.droppableProps}
                  ref={dropProvided.innerRef}
                  stickyHeader
                  sx={{
                    borderCollapse: "collapse",
                    width: "100%",
                    tableLayout: "fixed",
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {[
                        "STT",
                        "Tên layer",
                        "Mã layer",
                        "Ngày tạo",
                        "Đổi màu",
                        "Hành động",
                        "Hình ảnh",
                      ].map((col, idx) => (
                        <TableCell
                          key={idx}
                          align="center"
                          sx={{
                            fontSize: "16px",
                            fontWeight: "bold",
                            whiteSpace: "nowrap",
                            ...(col === "Hình ảnh" && {
                              width: 200,
                              minWidth: 200,
                            }),
                          }}
                        >
                          {col}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {layers
                      .sort((a, b) => Number(a.display_order || 0) - Number(b.display_order || 0))
                      .map((layer, index) => (
                        <Draggable
                          key={layer.layerId}
                          draggableId={layer.layerId.toString()}
                          index={index}
                        >
                          {(dragProvided) => (
                            <TableRow
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              {...dragProvided.dragHandleProps}
                              sx={{
                                backgroundColor: "white",
                                "&:hover": { backgroundColor: "#f5f5f5" },
                                borderBottom: "1px solid #e0e0e0",
                              }}
                            >
                              <TableCell align="center">{index + 1}</TableCell>
                              <TableCell align="center">
                                <TextField
                                  value={layer.layerName}
                                  onChange={(e) =>
                                    onEdit(layer.layerId, "layerName", e.target.value)
                                  }
                                  variant="standard"
                                  size="small"
                                  fullWidth
                                  inputProps={{ style: { textAlign: "center" } }}
                                  sx={{
                                    "& .MuiInput-underline:before": {
                                      borderBottom: "none",
                                    },
                                    "& .MuiInput-underline:hover:before": {
                                      borderBottom: "none",
                                    },
                                    "& .MuiInput-underline:after": {
                                      borderBottom: "none",
                                    },
                                  }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <TextField
                                  value={layer.layerCode}
                                  onChange={(e) =>
                                    onEdit(layer.layerCode, "layerCode", e.target.value)
                                  }
                                  variant="standard"
                                  size="small"
                                  fullWidth
                                  inputProps={{ style: { textAlign: "center" } }}
                                  sx={{
                                    "& .MuiInput-underline:before": {
                                      borderBottom: "none",
                                    },
                                    "& .MuiInput-underline:hover:before": {
                                      borderBottom: "none",
                                    },
                                    "& .MuiInput-underline:after": {
                                      borderBottom: "none",
                                    },
                                  }}
                                />
                              </TableCell>

                              <TableCell align="center">
                                {moment(layer.createTime).format("DD/MM/YYYY")}
                              </TableCell>
                              <TableCell align="center">
                                <Switch
                                  checked={layer.isColorChange}
                                  onChange={() =>
                                    onEdit(layer.layerId, "isColorChange", !layer.isColorChange)
                                  }
                                />
                              </TableCell>
                              <TableCell align="center">
                                <IconButton onClick={() => onDelete(layer.layerId)}>
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>

                              <TableCell align="center">
                                <ImageCustom
                                  url={
                                    typeof layer.imageUrl === "string"
                                      ? layer.imageUrl
                                      : layer.image instanceof File
                                      ? URL.createObjectURL(layer.image)
                                      : imageMap[layer.attachment?.encryptedId] || null
                                  }
                                  width={60}
                                  height={60}
                                />
                              </TableCell>
                            </TableRow>
                          )}
                        </Draggable>
                      ))}
                    {dropProvided.placeholder}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
    </Box>
  );
}

LayerTable.propTypes = {
  layers: PropTypes.arrayOf(
    PropTypes.shape({
      layerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      layerName: PropTypes.string.isRequired,
      layerCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      createTime: PropTypes.string,
      isColorChange: PropTypes.bool,
      attachment: PropTypes.shape({
        encryptedId: PropTypes.string,
      }),
    })
  ).isRequired,
  label: PropTypes.string,
  setLayers: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  droppableId: PropTypes.string,
  onDragEnd: PropTypes.func.isRequired,
};

LayerTable.defaultProps = {
  label: "",
  droppableId: "",
};

export default LayerTable;
